import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Heart, Music, Shirt, Trash2, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth, useCart } from "../App";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WishlistPage = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchWishlist();
  }, [user]);

  const fetchWishlist = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/wishlist`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setItems(res.data.items);
    } catch (e) {
      console.error("Failed to fetch wishlist");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (itemType, itemId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/wishlist/remove/${itemType}/${itemId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setItems(prev => prev.filter(i => !(i.item_id === itemId && i.item_type === itemType)));
      toast.success("Removed from wishlist");
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const handleAddToCart = async (item) => {
    const payload = item.item_type === "beat"
      ? { item_type: "beat", item_id: item.item_id, license_type: "basic", quantity: 1 }
      : { item_type: "merch", item_id: item.item_id, quantity: 1 };
    await addToCart(payload);
  };

  if (!user) {
    return (
      <div className="min-h-screen py-10 px-6" data-testid="wishlist-page">
        <div className="max-w-4xl mx-auto text-center py-20">
          <Heart className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h1 className="font-heading text-3xl uppercase mb-4">Wishlist</h1>
          <p className="text-zinc-400 mb-6">Please login to view your wishlist</p>
          <Button onClick={() => navigate("/")} className="bg-neon-cyan text-black">Go Home</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-neon-magenta border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-6" data-testid="wishlist-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <Badge className="bg-neon-magenta/20 text-neon-magenta border-neon-magenta/50 mb-4">Saved Items</Badge>
          <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight">
            Wishlist {items.length > 0 && `(${items.length})`}
          </h1>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 mb-6">Your wishlist is empty</p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/beats")} className="bg-neon-cyan text-black">Browse Beats</Button>
              <Button onClick={() => navigate("/merch")} variant="outline" className="border-neon-magenta text-neon-magenta">Browse Merch</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((item) => (
              <div key={`${item.item_type}-${item.item_id}`} className="glass p-4 group" data-testid={`wishlist-item-${item.item_id}`}>
                <Link to={item.item_type === "beat" ? `/beats/${item.item_id}` : `/merch/${item.item_id}`}>
                  <div className="aspect-square bg-zinc-800 overflow-hidden mb-3 relative">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {item.item_type === "beat" ? <Music className="w-8 h-8 text-zinc-600" /> : <Shirt className="w-8 h-8 text-zinc-600" />}
                      </div>
                    )}
                    <Badge className={`absolute top-2 left-2 text-xs ${item.item_type === "beat" ? "bg-neon-cyan/80 text-black" : "bg-neon-magenta/80 text-black"}`}>
                      {item.item_type === "beat" ? "Beat" : "Merch"}
                    </Badge>
                  </div>
                </Link>
                <p className="font-medium truncate mb-1">{item.name}</p>
                <p className={`font-mono font-bold ${item.item_type === "beat" ? "text-neon-cyan" : "text-neon-magenta"}`}>
                  ${item.price?.toFixed(2)}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={() => handleAddToCart(item)} className="flex-1 bg-white/10 hover:bg-white/20 text-xs" data-testid={`wishlist-add-cart-${item.item_id}`}>
                    <ShoppingCart className="w-3 h-3 mr-1" />Cart
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleRemove(item.item_type, item.item_id)} className="text-red-500 hover:text-red-400" data-testid={`wishlist-remove-${item.item_id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
